import type { PricingPlan } from './pricing';
import { CONTACT_EMAIL } from '../constants';
import { findFaqSection } from './useCaseSectionGuards';
import { normalizeUseCasePage } from './useCaseNormalize';
import type { UseCasePageData } from './useCaseQueries';
import { useCasePublicLabel } from './useCaseSanity';
import { AGENCIES_FAQ } from '../content/forAgenciesPage';
import { CREATORS_FAQ } from '../content/forCreatorsPage';
import { ENTERPRISE_FAQ } from '../content/forEnterprisePage';
import { FILMMAKERS_FAQ } from '../content/forFilmmakersPage';
import { MODELS_FAQ } from '../content/productModelsPage';
import { SCRIPT_FAQ } from '../content/productScriptPage';
import { AI_VIDEO_CONTINUITY_FAQ } from '../content/aiVideoContinuityPage';
import { SHOT_CONTROL_FAQ } from '../content/productShotControlPage';
import type { BreadcrumbItem } from '../content/breadcrumbs';
import {
  FAQ_BREADCRUMB,
  PRICING_BREADCRUMB,
  PRIVACY_BREADCRUMB,
  PRODUCT_BREADCRUMB,
  SHOWREEL_BREADCRUMB,
  SOLUTIONS_BREADCRUMB,
  TERMS_BREADCRUMB,
  USE_CASES_BREADCRUMB,
} from '../content/breadcrumbs';
import { getAllFaqItems, type FaqItem } from '../content/faq';
import {
  DEFAULT_DESCRIPTION,
  FAQ_META,
  FOR_AGENCIES_META,
  FOR_CREATORS_META,
  FOR_ENTERPRISE_META,
  FOR_FILMMAKERS_META,
  AI_VIDEO_CONTINUITY_META,
  ORG_ID,
  ORG_LOGO,
  PRICING_META,
  PRIVACY_META,
  PRODUCT_MODELS_META,
  PRODUCT_SCRIPT_META,
  PRODUCT_SHOT_CONTROL_META,
  SHOWREEL_META,
  SITE_NAME,
  SITE_URL,
  TERMS_META,
  pageUrl,
} from './seo';

function faqPageGraphNode(url: string, faqs: readonly FaqItem[]) {
  return {
    '@type': 'FAQPage' as const,
    '@id': `${url}#faq`,
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question' as const,
      name: faq.q,
      acceptedAnswer: { '@type': 'Answer' as const, text: faq.a },
    })),
  };
}

function breadcrumbGraphNode(pagePath: string, items: readonly BreadcrumbItem[]) {
  const url = pageUrl(pagePath);
  return {
    '@type': 'BreadcrumbList' as const,
    '@id': `${url}#breadcrumb`,
    itemListElement: items.map((item, index) => {
      const listItem: {
        '@type': 'ListItem';
        position: number;
        name: string;
        item?: string;
      } = {
        '@type': 'ListItem',
        position: index + 1,
        name: item.label,
      };
      if (item.to) {
        listItem.item = pageUrl(item.to);
      } else if (index === items.length - 1) {
        listItem.item = url;
      }
      return listItem;
    }),
  };
}

function webPageGraphNode(path: string, name: string, description: string) {
  const url = pageUrl(path);
  return {
    '@type': 'WebPage' as const,
    '@id': `${url}#webpage`,
    url,
    name,
    description,
    isPartOf: { '@id': WEBSITE_ID },
    publisher: { '@id': ORG_ID },
  };
}

function softwareApplicationGraphNode(
  path: string,
  description: string,
  extras?: Record<string, unknown>,
) {
  const url = pageUrl(path);
  return {
    '@type': 'SoftwareApplication' as const,
    '@id': `${url}#software`,
    name: SITE_NAME,
    applicationCategory: 'MultimediaApplication',
    operatingSystem: 'Web',
    url,
    description,
    publisher: { '@id': ORG_ID },
    ...extras,
  };
}

function baseGraph(extras: object[], options?: { fullOrganization?: boolean }) {
  const orgNode = options?.fullOrganization ? ORGANIZATION : ORGANIZATION_REFERENCE;
  return {
    '@context': 'https://schema.org' as const,
    '@graph': [orgNode, ...extras],
  };
}

const ORGANIZATION = {
  '@type': 'Organization',
  '@id': ORG_ID,
  name: SITE_NAME,
  url: SITE_URL,
  logo: ORG_LOGO,
  description: DEFAULT_DESCRIPTION,
  email: CONTACT_EMAIL,
  address: {
    '@type': 'PostalAddress',
    streetAddress: '737, Kheny Plaza, 4th Floor, Binnimangala, CMH Road, Indiranagar',
    addressLocality: 'Bengaluru',
    addressRegion: 'Karnataka',
    postalCode: '560038',
    addressCountry: 'IN',
  },
  sameAs: [
    'https://www.linkedin.com/company/getinduce-com/',
    'https://x.com/induceai',
    'https://www.instagram.com/induce.creators',
  ],
} as const;

const WEBSITE_ID = `${SITE_URL}/#website`;

const ORGANIZATION_REFERENCE = { '@id': ORG_ID };

const PRICING_OFFERS = [
  {
    '@type': 'Offer' as const,
    name: 'Basic',
    price: '49',
    priceCurrency: 'USD',
    description: 'For indie filmmakers and solo creators shooting out of their own kit.',
  },
  {
    '@type': 'Offer' as const,
    name: 'Studio',
    price: '129',
    priceCurrency: 'USD',
    description: 'For agencies and production teams shipping branded work on a clock.',
  },
  {
    '@type': 'Offer' as const,
    name: 'Enterprise',
    description: 'Custom infrastructure, API, and dedicated support for studios and streamers.',
  },
] as const;

export function buildHomePageSchema() {
  return baseGraph(
    [
      {
        '@type': 'WebSite',
        '@id': WEBSITE_ID,
        url: SITE_URL,
        name: SITE_NAME,
        publisher: { '@id': ORG_ID },
      },
      softwareApplicationGraphNode('/', DEFAULT_DESCRIPTION, {
        offers: PRICING_OFFERS,
      }),
    ],
    { fullOrganization: true },
  );
}

export function buildLegalPageSchema(title: string, path: string, lastUpdated: string) {
  const breadcrumbs = path === PRIVACY_META.path ? PRIVACY_BREADCRUMB : TERMS_BREADCRUMB;
  const description = path === PRIVACY_META.path ? PRIVACY_META.description : TERMS_META.description;
  return baseGraph([
    breadcrumbGraphNode(path, breadcrumbs),
    {
      ...webPageGraphNode(path, title, description),
      dateModified: lastUpdated,
    },
  ]);
}

function buildProductSubpageSchema(
  path: string,
  breadcrumbLabel: string,
  pageTitle: string,
  description: string,
  faqs: readonly FaqItem[],
) {
  const url = pageUrl(path);
  const breadcrumbs: BreadcrumbItem[] = [
    ...PRODUCT_BREADCRUMB,
    { label: breadcrumbLabel },
  ];
  return baseGraph([
    breadcrumbGraphNode(path, breadcrumbs),
    webPageGraphNode(path, pageTitle, description),
    softwareApplicationGraphNode(path, description),
    faqPageGraphNode(url, faqs),
  ]);
}

export function buildProductScriptPageSchema() {
  return buildProductSubpageSchema(
    PRODUCT_SCRIPT_META.path,
    'Script to Video',
    PRODUCT_SCRIPT_META.title,
    PRODUCT_SCRIPT_META.description,
    SCRIPT_FAQ,
  );
}

export function buildProductShotControlPageSchema() {
  return buildProductSubpageSchema(
    PRODUCT_SHOT_CONTROL_META.path,
    'Shot Control',
    PRODUCT_SHOT_CONTROL_META.title,
    PRODUCT_SHOT_CONTROL_META.description,
    SHOT_CONTROL_FAQ,
  );
}

export function buildProductModelsPageSchema() {
  return buildProductSubpageSchema(
    PRODUCT_MODELS_META.path,
    'AI Models',
    PRODUCT_MODELS_META.title,
    PRODUCT_MODELS_META.description,
    MODELS_FAQ,
  );
}

export function buildPricingPageSchema(plans: PricingPlan[] = []) {
  return baseGraph([
    breadcrumbGraphNode(PRICING_META.path, PRICING_BREADCRUMB),
    webPageGraphNode(PRICING_META.path, PRICING_META.title, PRICING_META.description),
    softwareApplicationGraphNode(PRICING_META.path, PRICING_META.description, {
      offers: plans.map(plan => ({
        '@type': 'Offer', name: plan.name, description: plan.description,
        price: plan.amount.toFixed(2), priceCurrency: plan.currency,
      })),
    }),
  ]);
}

export function buildFaqPageSchema() {
  const url = pageUrl(FAQ_META.path);
  return baseGraph([
    breadcrumbGraphNode(FAQ_META.path, FAQ_BREADCRUMB),
    webPageGraphNode(FAQ_META.path, FAQ_META.title, FAQ_META.description),
    faqPageGraphNode(url, getAllFaqItems()),
  ]);
}

export function buildShowreelPageSchema() {
  return baseGraph([
    breadcrumbGraphNode(SHOWREEL_META.path, SHOWREEL_BREADCRUMB),
    webPageGraphNode(SHOWREEL_META.path, SHOWREEL_META.title, SHOWREEL_META.description),
  ]);
}

type MarketingPageSchemaOptions = {
  breadcrumbs?: readonly BreadcrumbItem[];
  faqs?: readonly FaqItem[];
};

export function buildMarketingPageSchema(
  title: string,
  path: string,
  description: string,
  options?: MarketingPageSchemaOptions,
) {
  const url = pageUrl(path);
  const graph: object[] = [];
  if (options?.breadcrumbs?.length) {
    graph.push(breadcrumbGraphNode(path, options.breadcrumbs));
  }
  graph.push({
    ...webPageGraphNode(path, title, description),
    about: { '@id': ORG_ID },
  });
  if (options?.faqs?.length) {
    graph.push(faqPageGraphNode(url, options.faqs));
  }
  return baseGraph(graph);
}

function buildSolutionsPageSchema(
  meta: { path: string; title: string; description: string },
  breadcrumbLabel: string,
  faqs: readonly FaqItem[],
  softwareExtras?: Record<string, unknown>,
) {
  const url = pageUrl(meta.path);
  const breadcrumbs: BreadcrumbItem[] = [...SOLUTIONS_BREADCRUMB, { label: breadcrumbLabel }];
  return baseGraph([
    breadcrumbGraphNode(meta.path, breadcrumbs),
    webPageGraphNode(meta.path, meta.title, meta.description),
    softwareApplicationGraphNode(meta.path, meta.description, softwareExtras),
    faqPageGraphNode(url, faqs),
  ]);
}

export function buildAiVideoContinuityPageSchema() {
  return buildProductSubpageSchema(
    AI_VIDEO_CONTINUITY_META.path,
    'Continuity Engine',
    AI_VIDEO_CONTINUITY_META.title,
    AI_VIDEO_CONTINUITY_META.description,
    AI_VIDEO_CONTINUITY_FAQ,
  );
}

export function buildForFilmmakersPageSchema() {
  return buildSolutionsPageSchema(
    FOR_FILMMAKERS_META,
    'For Filmmakers',
    FILMMAKERS_FAQ,
    {
      applicationSubCategory: 'AI Filmmaking Platform',
      featureList: [
        'Screenplay-to-cut breakdown (FDX, PDF, Fountain)',
        'Persistent character and location continuity',
        'Autonomous cinematography agent',
        'Director-grade local shot override',
        'Multi-model orchestration',
        'Commercial licensing on every tier',
        'REST API and SDKs',
      ],
      offers: PRICING_OFFERS[0],
    },
  );
}

export function buildForAgenciesPageSchema() {
  return buildSolutionsPageSchema(FOR_AGENCIES_META, 'For Agencies', AGENCIES_FAQ, {
    applicationCategory: 'BusinessApplication',
    applicationSubCategory: 'AI Video Production Platform for Agencies',
    featureList: [
      'Brief-to-delivery script pipeline',
      'Per-client project isolation and brand continuity',
      'Multi-model orchestration',
      'Commercial licensing on every tier',
      'Training opt-out - client content stays yours',
    ],
    offers: PRICING_OFFERS[1],
  });
}

export function buildForEnterprisePageSchema() {
  return buildSolutionsPageSchema(FOR_ENTERPRISE_META, 'For Enterprise', ENTERPRISE_FAQ, {
    applicationCategory: 'BusinessApplication',
    applicationSubCategory: 'Enterprise AI Video Platform',
    featureList: [
      'Brief-to-delivery script pipeline',
      'SSO / SAML 2.0 and OAuth 2.0',
      'Departmental workspace isolation',
      'Role-based access control',
      'Audit logs and approval webhooks',
      'Training data opt-out on every tier',
    ],
    offers: PRICING_OFFERS[2],
  });
}

export function buildForCreatorsPageSchema() {
  return buildSolutionsPageSchema(FOR_CREATORS_META, 'For Creators', CREATORS_FAQ, {
    applicationSubCategory: 'AI Video Platform for Creators',
    featureList: [
      'Script-first editor with shot overrides',
      'Persistent character and location continuity',
      'Community continuity models',
      'Early access to new AI features',
      'Commercial licensing on every tier',
    ],
    offers: PRICING_OFFERS[0],
  });
}

export function buildUseCasePageSchema(page: UseCasePageData, seoImage?: string) {
  const path = `/use-cases/${page.slug}`;
  const breadcrumbs: BreadcrumbItem[] = [
    ...USE_CASES_BREADCRUMB,
    { label: useCasePublicLabel(page) },
  ];
  const url = pageUrl(path);
  const graph: object[] = [
    breadcrumbGraphNode(path, breadcrumbs),
    {
      ...webPageGraphNode(path, page.seoTitle, page.seoDescription),
      datePublished: page.publishedAt,
      dateModified: page._updatedAt ?? page.publishedAt,
    },
    softwareApplicationGraphNode(path, page.seoDescription),
  ];
  if (seoImage) {
    graph[1] = { ...graph[1], primaryImageOfPage: seoImage };
  }
  const faqSection = findFaqSection(normalizeUseCasePage(page));
  if (faqSection?._type === 'ucFaq' && faqSection.items?.length) {
    graph.push(
      faqPageGraphNode(
        url,
        faqSection.items.map((item) => ({ q: item.question, a: item.answer })),
      ),
    );
  }
  return baseGraph(graph);
}
